import { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { FREQ_LABELS } from "../lib/format";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { UserPlus, Link2, FileText, Check, X, Clock, Copy, Mail, Trash2 } from "lucide-react";
import { useSort, SortBtn } from "../lib/sortable";
import { useAuth } from "../context/AuthContext";

const SUB_A = {
  name: (s) => `${(s.section_a || {}).first_name || ""} ${(s.section_a || {}).last_name || ""}`.trim(),
  submitted: (s) => s.submitted_at || "",
  docs: (s) => (s.files || []).length,
  status: (s) => s.status || "",
};
const INV_A = {
  email: (i) => i.email || "", sent: (i) => i.created_at || "",
  expires: (i) => i.expires_at || "", status: (i) => i.status || "",
};

const BPAF_2026 = 16452;
const BPAON_2026 = 12989;

const KIND_LABELS = {
  sin_id: "SIN + Government ID", void_cheque: "Void Cheque / DD Form",
  td1_federal: "Signed TD1 Federal", td1_ontario: "Signed TD1 Ontario",
};

const EMPTY_B = {
  pay_type: "salary", annual_salary: "", hourly_rate: "", standard_hours: 80,
  standard_hours_per_week: "", pay_frequency: "biweekly", pay_method: "Direct Deposit",
  job_title: "", employment_type: "Full-Time", hire_date: "", vacation_rate: 4,
  vacation_mode: "accrue", vacation_accrual_method: "", vacation_pay_start_date: "",
  cpp_applicable: true, ei_applicable: true, ei_insurable_type: "Insurable",
  wsib_applicable: false, wsib_code: "", health_dental: false, rrsp_pension: false,
  union_dues: false, other_deductions_note: "", rpp_contribution: 0,
  additional_tax_per_pay: 0, internal_notes: "", employee_number: "",
  td1_federal_claim: "", td1_provincial_claim: "",
};

export default function OnboardingPanel({ companyId, canEdit, canManage, onApproved }) {
  const { user } = useAuth();
  const isMaster = user?.role === "super_admin" && (user?.staff_role || "master") === "master";
  const [subs, setSubs] = useState([]);
  const [linkBusy, setLinkBusy] = useState(false);
  const [review, setReview] = useState(null);   // submission being reviewed
  const [b, setB] = useState(EMPTY_B);
  const [busy, setBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmails, setInviteEmails] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [expiryDays, setExpiryDays] = useState(14);
  const [invites, setInvites] = useState([]);

  const load = useCallback(() => {
    api.get(`/companies/${companyId}/onboarding`).then((r) => setSubs(r.data)).catch(() => {});
  }, [companyId]);
  const loadInvites = useCallback(() => {
    if (!canManage) return;
    api.get(`/companies/${companyId}/onboarding-invites`).then((r) => setInvites(r.data)).catch(() => {});
  }, [companyId, canManage]);
  useEffect(() => { if (companyId) { load(); loadInvites(); } }, [companyId, load, loadInvites]);

  const pending = subs.filter((s) => s.status === "pending");
  const subSort = useSort(subs, SUB_A, "submitted", "desc");
  const invSort = useSort(invites, INV_A, "sent", "desc");

  const copyLink = async () => {
    setLinkBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/onboarding-link?expiry_days=${expiryDays}`);
      await navigator.clipboard.writeText(data.url).catch(() => {});
      toast.success(`New one-time link copied — works once, expires in ${expiryDays} days`);
      loadInvites();
    } catch (e) { toast.error(apiErr(e)); } finally { setLinkBusy(false); }
  };

  const copyInviteLink = async (inv) => {
    await navigator.clipboard.writeText(inv.url).catch(() => {});
    toast.success("Onboarding link copied");
  };
  const resendInvite = async (inv) => {
    try {
      await api.post(`/onboarding-invites/${inv.invite_id}/resend?expiry_days=${expiryDays}`);
      toast.success(`Invite resent to ${inv.email}`);
      loadInvites();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const revokeInvite = async (inv) => {
    if (!window.confirm("Revoke this onboarding link? It will stop working immediately.")) return;
    try {
      await api.post(`/onboarding-invites/${inv.invite_id}/revoke`);
      toast.success("Invite revoked");
      loadInvites();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const deleteInvite = async (inv) => {
    if (!window.confirm(`Permanently delete the invite for ${inv.email || "this link"}? This removes it from the list and cannot be undone.`)) return;
    try {
      await api.delete(`/onboarding-invites/${inv.invite_id}`);
      toast.success("Invite deleted");
      loadInvites();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openReview = (s) => {
    const a = s.section_a || {};
    const basicOnly = a.basic_personal_only === true;
    setB({
      ...EMPTY_B,
      hire_date: a.date_signed || "",
      additional_tax_per_pay: a.additional_tax_per_pay || 0,
      td1_federal_claim: basicOnly ? BPAF_2026 : "",
      td1_provincial_claim: basicOnly ? BPAON_2026 : "",
    });
    setReview(s);
  };

  const sendInvite = async () => {
    const emails = inviteEmails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (!emails.length) { toast.error("Enter at least one email address"); return; }
    setInviteBusy(true);
    try {
      if (emails.length === 1) {
        await api.post(`/companies/${companyId}/onboarding-invite`, { email: emails[0], name: inviteName, expiry_days: expiryDays });
        toast.success(`Onboarding invite emailed to ${emails[0]}`);
      } else {
        const { data } = await api.post(`/companies/${companyId}/onboarding-invite-bulk`, { emails, expiry_days: expiryDays });
        toast.success(`Sent ${data.sent} of ${data.total} onboarding invites`);
      }
      setInviteOpen(false);
      setInviteEmails(""); setInviteName("");
      loadInvites();
    } catch (e) { toast.error(apiErr(e)); } finally { setInviteBusy(false); }
  };

  const viewFile = async (fileId, name) => {
    try {
      const res = await api.get(`/onboarding/files/${fileId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/onboarding/submission/${review.onboarding_id}/approve`, {
        ...b,
        annual_salary: parseFloat(b.annual_salary) || 0,
        hourly_rate: parseFloat(b.hourly_rate) || 0,
        standard_hours: parseFloat(b.standard_hours) || 0,
        standard_hours_per_week: parseFloat(b.standard_hours_per_week) || 0,
        vacation_rate: parseFloat(b.vacation_rate) || 0,
        rpp_contribution: parseFloat(b.rpp_contribution) || 0,
        additional_tax_per_pay: parseFloat(b.additional_tax_per_pay) || 0,
        td1_federal_claim: b.td1_federal_claim === "" ? null : parseFloat(b.td1_federal_claim),
        td1_provincial_claim: b.td1_provincial_claim === "" ? null : parseFloat(b.td1_provincial_claim),
      });
      toast.success("Employee created from onboarding submission");
      setReview(null);
      load();
      onApproved && onApproved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const reject = async (s) => {
    if (!window.confirm("Reject this onboarding submission?")) return;
    try { await api.post(`/onboarding/submission/${s.onboarding_id}/reject`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const setB_ = (k) => (e) => setB((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const a = review?.section_a || {};

  return (
    <div className="border border-border bg-white mt-6" data-testid="onboarding-panel">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        <UserPlus className="h-4 w-4" />
        <h2 className="font-bold tracking-tight">Onboarding / Pending Employees</h2>
        {pending.length > 0 && <span className="text-[10px] font-mono uppercase tracking-widest bg-amber-100 text-amber-700 px-2 py-1">{pending.length} pending</span>}
        {canManage && (
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="email-onboarding-invite" onClick={() => setInviteOpen(true)} className="inline-flex items-center gap-2 border border-black bg-black text-white px-3 py-2 text-xs font-medium hover:bg-neutral-800 transition-colors">
              <Mail className="h-3.5 w-3.5" /> Invite hires
            </button>
            <button data-testid="copy-onboarding-link" onClick={copyLink} disabled={linkBusy} className="inline-flex items-center gap-2 border border-black px-3 py-2 text-xs font-medium hover:bg-black hover:text-white transition-colors disabled:opacity-50">
              <Link2 className="h-3.5 w-3.5" /> {linkBusy ? "…" : "One-time link"}
            </button>
          </div>
        )}
      </div>

      {subs.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <th className="px-5 py-3"><SortBtn label="Name" col="name" sort={subSort.sort} onSort={subSort.toggle} /></th>
                <th className="px-5 py-3"><SortBtn label="Submitted" col="submitted" sort={subSort.sort} onSort={subSort.toggle} /></th>
                <th className="px-5 py-3"><SortBtn label="Documents" col="docs" sort={subSort.sort} onSort={subSort.toggle} /></th>
                <th className="px-5 py-3"><SortBtn label="Status" col="status" sort={subSort.sort} onSort={subSort.toggle} /></th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subSort.sorted.map((s) => {
                const sa = s.section_a || {};
                return (
                <tr key={s.onboarding_id} data-testid={`onboarding-row-${s.onboarding_id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="px-5 py-3">
                    <div className="font-medium">{sa.first_name} {sa.last_name}</div>
                    <div className="text-xs font-mono text-muted-foreground">{sa.email || "—"}</div>
                  </td>
                  <td className="px-5 py-3 text-xs font-mono text-muted-foreground">{(s.submitted_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-3 text-xs">{(s.files || []).length} file{(s.files || []).length === 1 ? "" : "s"}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 ${s.status === "pending" ? "bg-amber-100 text-amber-700" : s.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"}`}>{s.status}</span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {s.status === "pending" ? (
                        <button data-testid={`review-onboarding-${s.onboarding_id}`} onClick={() => openReview(s)} className="h-8 inline-flex items-center gap-1.5 border border-black px-3 text-xs hover:bg-black hover:text-white transition-colors">
                          <FileText className="h-3.5 w-3.5" /> Review
                        </button>
                      ) : (
                        <button data-testid={`view-onboarding-${s.onboarding_id}`} onClick={() => setReview(s)} className="h-8 inline-flex items-center gap-1.5 border border-border px-3 text-xs hover:border-black transition-colors">
                          <FileText className="h-3.5 w-3.5" /> View
                        </button>
                      )}
                      {canEdit && s.status === "pending" && (
                        <button data-testid={`reject-onboarding-${s.onboarding_id}`} onClick={() => reject(s)} title="Reject" className="h-8 w-8 flex items-center justify-center border border-border hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-5 py-12 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
          <Clock className="h-5 w-5 text-muted-foreground" />
          No onboarding submissions yet.{canManage && " Share the onboarding link with a new hire to get started."}
        </div>
      )}

      {/* Sent invites tracking */}
      {canManage && invites.length > 0 && (
        <div className="border-t border-border" data-testid="sent-invites">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
            <h3 className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Sent Invites</h3>
            <span className="ml-auto text-[10px] font-mono text-muted-foreground">{invites.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  <th className="px-5 py-2.5"><SortBtn label="Email" col="email" sort={invSort.sort} onSort={invSort.toggle} /></th>
                  <th className="px-5 py-2.5"><SortBtn label="Sent" col="sent" sort={invSort.sort} onSort={invSort.toggle} /></th>
                  <th className="px-5 py-2.5"><SortBtn label="Expires" col="expires" sort={invSort.sort} onSort={invSort.toggle} /></th>
                  <th className="px-5 py-2.5"><SortBtn label="Status" col="status" sort={invSort.sort} onSort={invSort.toggle} /></th>
                  <th className="px-5 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invSort.sorted.map((inv) => (
                  <tr key={inv.invite_id} data-testid={`invite-row-${inv.invite_id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                    <td className="px-5 py-2.5">{inv.email || <span className="text-muted-foreground italic">link only</span>}</td>
                    <td className="px-5 py-2.5 text-xs font-mono text-muted-foreground">{(inv.created_at || "").slice(0, 10)}</td>
                    <td className="px-5 py-2.5 text-xs font-mono text-muted-foreground">{(inv.expires_at || "").slice(0, 10)}</td>
                    <td className="px-5 py-2.5"><InviteStatus status={inv.status} /></td>
                    <td className="px-5 py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        <button data-testid={`invite-copy-${inv.invite_id}`} onClick={() => copyInviteLink(inv)} title="Copy link" className="h-7 w-7 flex items-center justify-center border border-border hover:border-black hover:bg-black hover:text-white transition-colors"><Copy className="h-3.5 w-3.5" /></button>
                        {inv.status !== "used" && (
                          <>
                            {inv.email && (
                              <button data-testid={`invite-resend-${inv.invite_id}`} onClick={() => resendInvite(inv)} title="Resend email" className="h-7 w-7 flex items-center justify-center border border-border hover:border-black hover:bg-black hover:text-white transition-colors"><Mail className="h-3.5 w-3.5" /></button>
                            )}
                            {inv.status !== "revoked" && (
                              <button data-testid={`invite-revoke-${inv.invite_id}`} onClick={() => revokeInvite(inv)} title="Revoke link" className="h-7 w-7 flex items-center justify-center border border-border hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors"><X className="h-3.5 w-3.5" /></button>
                            )}
                          </>
                        )}
                        {isMaster && (
                          <button data-testid={`invite-delete-${inv.invite_id}`} onClick={() => deleteInvite(inv)} title="Delete invite" className="h-7 w-7 flex items-center justify-center border border-border hover:border-red-700 hover:bg-red-700 hover:text-white transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Review dialog */}
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="rounded-none border-black w-[96vw] sm:max-w-[1200px] max-h-[94vh] overflow-y-auto" data-testid="onboarding-review-dialog">
          <DialogHeader>
            <DialogTitle className="tracking-tight">{a.first_name} {a.last_name}</DialogTitle>
            <DialogDescription className="text-xs">Review the employee's submission (Section A) and complete payroll setup (Section B) to create the employee.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="border border-border">
              <div className="px-4 py-2 bg-neutral-100 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Section A — Employee submission</div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <RO k="Preferred name" v={a.preferred_name} />
                <RO k="Email" v={a.email} />
                <RO k="Phone" v={a.phone} />
                <RO k="Date of birth" v={a.date_of_birth} />
                <RO k="SIN" v={a.sin} />
                <RO k="Address" v={[a.address_line1, a.apartment_unit, a.city, a.province, a.postal_code].filter(Boolean).join(", ")} />
                <RO k="Bank" v={a.bank_name} />
                <RO k="Institution / Transit / Acct" v={[a.bank_institution, a.bank_transit, a.bank_account].filter(Boolean).join(" / ")} />
                <RO k="TD1 Federal done" v={fmtYN(a.td1_federal_completed)} />
                <RO k="TD1 Ontario done" v={fmtYN(a.td1_provincial_completed)} />
                <RO k="Basic personal only" v={fmtYN(a.basic_personal_only)} />
                <RO k="Additional tax / pay" v={a.additional_tax_per_pay ? `$${a.additional_tax_per_pay}` : "—"} />
                <RO k="Emergency contact" v={[a.emergency_contact_name, a.emergency_contact_relationship, a.emergency_contact_phone].filter(Boolean).join(" · ")} />
                <RO k="Signature" v={a.signature} />
                <RO k="Date signed" v={a.date_signed} />
                <RO k="Consent" v={a.consent ? "Agreed" : "—"} />
              </div>
              {(review?.files || []).length > 0 && (
                <div className="px-4 pb-4 flex flex-wrap gap-2">
                  {review.files.map((f) => (
                    <button key={f.file_id} data-testid={`onboarding-file-${f.file_id}`} onClick={() => viewFile(f.file_id, f.original_filename)} className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs hover:border-black hover:bg-black hover:text-white transition-colors">
                      <FileText className="h-3.5 w-3.5" /> {KIND_LABELS[f.kind] || f.kind}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {review?.status === "pending" && canEdit && (
              <div className="border border-black">
                <div className="px-4 py-2 bg-black text-white text-[10px] font-mono uppercase tracking-widest">Section B — Payroll setup (staff)</div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <BI label="Job Title" testid="rb-job-title" value={b.job_title} onChange={setB_("job_title")} />
                  <BS label="Employment Type" testid="rb-emp-type" value={b.employment_type} onChange={setB_("employment_type")} opts={["Full-Time", "Part-Time", "Contract", "Temporary", "Seasonal", "Other"]} />
                  <BI label="Start / Hire Date" type="date" testid="rb-hire-date" value={b.hire_date} onChange={setB_("hire_date")} />
                  <BS label="Pay Method" testid="rb-pay-method" value={b.pay_method} onChange={setB_("pay_method")} opts={["Direct Deposit", "Cheque", "Other"]} />
                  <BS label="Pay Type" testid="rb-pay-type" value={b.pay_type} onChange={setB_("pay_type")} opts={[["salary", "Salary"], ["hourly", "Hourly"]]} />
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Pay Frequency</label>
                    <select data-testid="rb-frequency" value={b.pay_frequency} onChange={setB_("pay_frequency")} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
                      {Object.entries(FREQ_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  {b.pay_type === "salary" ? (
                    <BI label="Annual Salary ($)" type="number" testid="rb-annual" value={b.annual_salary} onChange={setB_("annual_salary")} />
                  ) : (
                    <>
                      <BI label="Hourly Rate ($/hr)" type="number" testid="rb-hourly" value={b.hourly_rate} onChange={setB_("hourly_rate")} />
                      <BI label="Hours / Pay Period" type="number" testid="rb-hours-period" value={b.standard_hours} onChange={setB_("standard_hours")} />
                    </>
                  )}
                  <BI label="Standard Hours / Week" type="number" testid="rb-hours-week" value={b.standard_hours_per_week} onChange={setB_("standard_hours_per_week")} />
                  <BI label="Vacation Rate %" type="number" testid="rb-vac-rate" value={b.vacation_rate} onChange={setB_("vacation_rate")} />
                  <BS label="Vacation Policy" testid="rb-vac-mode" value={b.vacation_mode} onChange={setB_("vacation_mode")} opts={[["accrue", "Accrue (bank it)"], ["payout", "Pay out each period"]]} />
                  <BS label="Vacation Accrual Method" testid="rb-vac-accrual" value={b.vacation_accrual_method} onChange={setB_("vacation_accrual_method")} opts={["", "Accrue Each Pay Period", "Annual Lump Sum", "Other"]} />
                  <BI label="Vacation Pay Start Date" type="date" testid="rb-vac-start" value={b.vacation_pay_start_date} onChange={setB_("vacation_pay_start_date")} />
                  <BS label="EI Insurable Earnings Type" testid="rb-ei-type" value={b.ei_insurable_type} onChange={setB_("ei_insurable_type")} opts={["Insurable", "Non-Insurable"]} />
                  <BI label="WSIB Classification Code" testid="rb-wsib-code" value={b.wsib_code} onChange={setB_("wsib_code")} />
                  <BI label="RPP / pre-tax per period ($)" type="number" testid="rb-rpp" value={b.rpp_contribution} onChange={setB_("rpp_contribution")} />
                  <BI label="Additional Tax / Pay ($)" type="number" testid="rb-addl-tax" value={b.additional_tax_per_pay} onChange={setB_("additional_tax_per_pay")} />
                  <BI label="TD1 Federal claim (auto if basic only)" type="number" testid="rb-td1-fed" value={b.td1_federal_claim} onChange={setB_("td1_federal_claim")} />
                  <BI label="TD1 Ontario claim (auto if basic only)" type="number" testid="rb-td1-on" value={b.td1_provincial_claim} onChange={setB_("td1_provincial_claim")} />
                  <div className="sm:col-span-2 flex flex-wrap gap-x-6 gap-y-2 pt-1">
                    <CB label="CPP Applicable" testid="rb-cpp" checked={b.cpp_applicable} onChange={setB_("cpp_applicable")} />
                    <CB label="EI Applicable" testid="rb-ei" checked={b.ei_applicable} onChange={setB_("ei_applicable")} />
                    <CB label="WSIB Applicable" testid="rb-wsib" checked={b.wsib_applicable} onChange={setB_("wsib_applicable")} />
                    <CB label="Health/Dental" testid="rb-health" checked={b.health_dental} onChange={setB_("health_dental")} />
                    <CB label="RRSP/Pension" testid="rb-rrsp" checked={b.rrsp_pension} onChange={setB_("rrsp_pension")} />
                    <CB label="Union Dues" testid="rb-union" checked={b.union_dues} onChange={setB_("union_dues")} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Internal Notes</label>
                    <textarea data-testid="rb-notes" value={b.internal_notes} onChange={setB_("internal_notes")} rows={2} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {review?.status === "pending" && canEdit && (
            <DialogFooter className="gap-2">
              <button data-testid="reject-review-button" onClick={() => reject(review)} className="inline-flex items-center gap-2 border border-border px-4 py-3 text-sm font-medium hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors">
                <X className="h-4 w-4" /> Reject
              </button>
              <button data-testid="approve-review-button" disabled={busy} onClick={approve} className="flex-1 bg-black text-white py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
                <Check className="h-4 w-4" /> {busy ? "Creating…" : "Approve & create employee"}
              </button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Email invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="rounded-none border-black w-[95vw] sm:max-w-xl max-h-[94vh] overflow-y-auto" data-testid="onboarding-invite-dialog">
          <DialogHeader>
            <DialogTitle className="tracking-tight">Invite new hires</DialogTitle>
            <DialogDescription className="text-xs">Each hire gets their own single-use link (expires in 14 days). Add one email, or paste several — one per line — to invite everyone at once.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">New hire email(s) *</label>
              <textarea data-testid="invite-emails" rows={4} value={inviteEmails} onChange={(e) => setInviteEmails(e.target.value)} placeholder={"ava@example.com\nnoah@example.com"} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
            </div>
            <BI label="Name (optional — used for a single invite)" testid="invite-name" value={inviteName} onChange={(e) => setInviteName(e.target.value)} />
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Link valid for</label>
              <select data-testid="invite-expiry" value={expiryDays} onChange={(e) => setExpiryDays(Number(e.target.value))} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="send-invite-button" disabled={inviteBusy || !inviteEmails.trim()} onClick={sendInvite} className="w-full bg-black text-white py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
              <Mail className="h-4 w-4" /> {inviteBusy ? "Sending…" : "Send invite(s)"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function fmtYN(v) { return v === true ? "Yes" : v === false ? "No" : "—"; }
const INVITE_BADGE = {
  pending: "bg-amber-100 text-amber-700", opened: "bg-blue-100 text-blue-700",
  used: "bg-emerald-100 text-emerald-700", expired: "bg-neutral-200 text-neutral-600",
  revoked: "bg-red-100 text-red-700",
};
function InviteStatus({ status }) {
  return <span data-testid={`invite-status-${status}`} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 ${INVITE_BADGE[status] || "bg-neutral-200 text-neutral-600"}`}>{status}</span>;
}
function RO({ k, v }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-border/60 last:border-0">
      <span className="text-muted-foreground text-xs whitespace-nowrap">{k}</span>
      <span className="text-right break-words">{v || "—"}</span>
    </div>
  );
}
function BI({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
    </div>
  );
}
function BS({ label, value, onChange, testid, opts }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
      <select data-testid={testid} value={value} onChange={onChange} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
        {opts.map((o) => Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o || "Please select"}</option>)}
      </select>
    </div>
  );
}
function CB({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" data-testid={testid} checked={!!checked} onChange={onChange} className="accent-black h-4 w-4" /> {label}
    </label>
  );
}
